import React, { InputHTMLAttributes, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import { getCharacterCountText } from '@adsk/alloy-react-helpers';
import { isWeaveTheme, useTheme } from '@adsk/alloy-react-styles';
import {
  DetailedStylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { ICON_SIZE, TEXT_INPUT_TYPES } from './consts';
import { StyledDiv, StyledInput } from './Elements';
import { useDeprecatedRenderProps, useTextInput } from './hooks';
import { RenderProps } from './hooks/useDeprecatedRenderProps';
import { NonTextInputProps, RenderElementType, TextInputType } from './types';

type TextInputProps = DetailedStylableComponent<
  Omit<InputHTMLAttributes<HTMLInputElement>, NonTextInputProps | 'value'>,
  HTMLInputElement
> &
  RenderProps & {
    type?: TextInputType;
    disabled?: boolean;
    error?: boolean;
    value?: string;
    softLimit?: number;
    showCountThreshold?: number;
    showCount?: boolean;
    inputClassName?: string;
    selectTextOnFocus?: boolean;
    'data-state'?: string;
    'data-testid'?: string;
  };

/**
 * A TextInput component that optionally displays an icon.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to the HTML input element.
 */

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      type = TEXT_INPUT_TYPES.TEXT,
      style,
      className,
      disabled = false,
      error = false,
      renderIcon,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
      onFocus,
      selectTextOnFocus,
      onKeyDown,
      onChange,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      value,
      defaultValue,
      showCount,
      inputClassName,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const textInputTokens = theme.tokens?.component?.input;
    const isWeave = isWeaveTheme(theme);

    const getAdornmentStyle = useCallback(
      (isDisabled?: boolean, hasError?: boolean): CSSObject => ({
        color: hasError
          ? textInputTokens?.error?.icon?.fill
          : textInputTokens?.color,
        pointerEvents: 'none',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        opacity: isDisabled ? textInputTokens?.opacity?.disabled : 1,
      }),
      [textInputTokens],
    );

    const { renderLeft, renderRight } = useDeprecatedRenderProps({
      renderIcon,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
    });

    const {
      refs: { divRef, leftRef, rightRef },
      padding: { paddingLeft, paddingRight },
      propGetters: { getInputProps },
      charCount,
      shouldRenderCount,
    } = useTextInput({
      renderLeft,
      renderRight,
      onFocus,
      selectTextOnFocus,
      onKeyDown,
      onChange,
      type,
      ref,
      showCount,
      softLimit,
      maxLength,
      showCountThreshold,
      value,
      defaultValue,
    });

    const renderCount: RenderElementType = useCallback(
      (props) => {
        if (shouldRenderCount) {
          return (
            <div {...props} data-testid="TextInput__count">
              {getCharacterCountText({ charCount, softLimit, maxLength })}
            </div>
          );
        }

        return null;
      },
      [softLimit, charCount, maxLength, shouldRenderCount],
    );

    const renderRightComponent = useMemo(() => {
      return renderRight ?? renderCount;
    }, [renderRight, renderCount]);

    return (
      <StyledDiv
        $isWeave={isWeave}
        $tokens={textInputTokens}
        $halo={theme?.tokens?.semantic?.halo}
        ref={divRef}
        className={className}
        $disabled={disabled}
        $error={error}
        css={style || {}}
        data-state={props['data-state']}
      >
        {renderLeft?.({
          size: ICON_SIZE,
          ref: leftRef,
          style: {
            ...getAdornmentStyle(disabled, error),
            left: paddingLeft,
          },
        })}
        <StyledInput
          {...props}
          {...getInputProps()}
          disabled={disabled}
          maxLength={maxLength}
          className={inputClassName}
          $tokens={textInputTokens}
          $isWeave={isWeave}
          data-testid={dataTestId}
        />
        {renderRightComponent?.({
          size: ICON_SIZE,
          ref: rightRef,
          style: {
            ...getAdornmentStyle(disabled, error),
            right: paddingRight,
          },
        })}
      </StyledDiv>
    );
  },
);

TextInput.displayName = 'TextInput';

TextInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Deprecated: Display icon adjacent to input text on right side */
  renderIcon: PropTypes.func,
  /** Deprecated: Display icon adjacent to input text on left side */
  renderLeftIcon: PropTypes.func,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Type of input value */
  type: PropTypes.oneOf(Object.values(TEXT_INPUT_TYPES)),
  /** Display a React element adjacent to input text on left side */
  renderLeftElement: PropTypes.func,
  /** Display a React element adjacent to input text on right side */
  renderRightElement: PropTypes.func,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** Class applied to the input element */
  inputClassName: PropTypes.string,
  /** If true, the input will select all text when it is focused */
  selectTextOnFocus: PropTypes.bool,
};

export default Object.assign(TextInput, {
  TYPES: TEXT_INPUT_TYPES,
});
